import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { TrendingUp, TrendingDown, Target, Zap, Trophy, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicStats() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setData(null);
    fetch(`${API}/api/public/stats?days=${days}&_t=${Date.now()}`)
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setError(String(e)));
  }, [days]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-400" data-testid="stats-error">
        Error al cargar estadísticas: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-black p-8">
        <Skeleton className="h-64 w-full max-w-4xl mx-auto" />
      </div>
    );
  }

  const roiPositive = data.roi_pct >= 0;
  const streakColor =
    data.current_streak?.type === "won" ? "bg-green-500" : "bg-red-500";

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-900 to-black text-white" data-testid="public-stats-page">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black tracking-tight">
            📊 <span className="text-yellow-400">BetDataLab</span>
          </h1>
          <p className="mt-3 text-lg text-zinc-400 max-w-2xl mx-auto">
            Transparencia total. Todos los picks. Todos los resultados.
            <br />
            <span className="text-xs text-zinc-500">Últimos {days} días · Actualizado en tiempo real</span>
          </p>

          <div className="mt-6 flex justify-center gap-2 flex-wrap">
            {[7, 14, 30, 90].map((d) => (
              <Button
                key={d}
                data-testid={`days-filter-${d}`}
                variant={days === d ? "default" : "outline"}
                size="sm"
                onClick={() => setDays(d)}
                className={days === d ? "bg-yellow-400 text-black hover:bg-yellow-300" : ""}
              >
                {d}d
              </Button>
            ))}
          </div>
        </header>

        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Card className="bg-zinc-900/60 border-zinc-800" data-testid="stat-roi">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                {roiPositive ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                ROI
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-4xl font-black ${roiPositive ? "text-green-400" : "text-red-400"}`}>
                {roiPositive ? "+" : ""}{data.roi_pct}%
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {data.profit_units > 0 ? "+" : ""}{data.profit_units}u beneficio
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-zinc-800" data-testid="stat-winrate">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Target className="w-4 h-4 text-yellow-400" /> Winrate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-black text-yellow-400">{data.winrate_pct}%</div>
              <div className="text-xs text-zinc-500 mt-1">
                {data.wins}W · {data.losses}L
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-zinc-800" data-testid="stat-total">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-400" /> Picks
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-black">{data.total_picks}</div>
              <div className="text-xs text-zinc-500 mt-1">{data.total_staked_units}u apostadas</div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/60 border-zinc-800" data-testid="stat-streak">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-purple-400" /> Racha actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-baseline gap-2">
                <div className={`text-4xl font-black ${data.current_streak?.type === "won" ? "text-green-400" : "text-red-400"}`}>
                  {data.current_streak?.count || 0}
                </div>
                <Badge className={streakColor}>{data.current_streak?.type === "won" ? "WINS" : "LOSSES"}</Badge>
              </div>
              {data.best_day && (
                <div className="text-xs text-zinc-500 mt-1">
                  Mejor día: {data.best_day.date} ({data.best_day.roi > 0 ? "+" : ""}{data.best_day.roi}%)
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <section className="mb-10" data-testid="days-table">
          <h2 className="text-2xl font-bold mb-4">📅 Día a día</h2>
          <div className="bg-zinc-900/60 rounded-xl border border-zinc-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-xs">
                <tr>
                  <th className="p-3 text-left">Fecha</th>
                  <th className="p-3 text-center">W</th>
                  <th className="p-3 text-center">L</th>
                  <th className="p-3 text-right">Profit</th>
                  <th className="p-3 text-right">ROI</th>
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.date} className="border-t border-zinc-800 hover:bg-zinc-800/40">
                    <td className="p-3 font-mono text-xs">{d.date}</td>
                    <td className="p-3 text-center text-green-400 font-bold">{d.w}</td>
                    <td className="p-3 text-center text-red-400 font-bold">{d.l}</td>
                    <td className={`p-3 text-right font-mono ${d.profit >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {d.profit >= 0 ? "+" : ""}{d.profit}u
                    </td>
                    <td className={`p-3 text-right font-bold ${d.roi >= 0 ? "text-green-400" : "text-red-400"}`}>
                      {d.roi >= 0 ? "+" : ""}{d.roi}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-10" data-testid="recent-picks">
          <h2 className="text-2xl font-bold mb-4">🎯 Últimos picks</h2>
          <div className="space-y-2">
            {data.recent_picks.map((p, i) => (
              <div
                key={i}
                className="flex items-center justify-between bg-zinc-900/60 border border-zinc-800 rounded-lg p-3"
              >
                <div>
                  <div className="text-sm font-semibold">{p.match}</div>
                  <div className="text-xs text-zinc-500">
                    {p.prediction} · @{p.odds} · {p.league}
                  </div>
                </div>
                <Badge className={p.result === "won" ? "bg-green-500" : "bg-red-500"}>
                  {p.result === "won" ? "✓ WIN" : "✗ LOSS"}
                </Badge>
              </div>
            ))}
          </div>
        </section>

        <section className="text-center py-12 bg-gradient-to-r from-yellow-500/10 via-yellow-400/10 to-yellow-500/10 rounded-2xl border border-yellow-400/30">
          <Lock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-3xl font-black mb-2">¿Quieres los picks antes que nadie?</h2>
          <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
            Únete al canal VIP y recibe cada pick con análisis completo, alertas de valor y stakes optimizados.
          </p>
          <Button
            data-testid="vip-signup-cta"
            size="lg"
            className="bg-yellow-400 text-black hover:bg-yellow-300 font-bold text-lg px-8"
            onClick={() => window.open(data.vip_signup_url, "_blank")}
          >
            🔓 Acceder al VIP · 29,99€/mes
          </Button>
        </section>

        <footer className="text-center text-xs text-zinc-600 mt-12">
          @betdatalab · Datos actualizados {new Date(data.generated_at).toLocaleString("es-ES")}
          <br />
          Juega responsable · +18
        </footer>
      </div>
    </div>
  );
}
